import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BiEdit } from "react-icons/bi";
import { IoIosAddCircle } from "react-icons/io";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import ToggleSwitch from "../../../component/ToggleSwitch";
import {
  useGetBanksQuery,
  useUpdateBankMutation,
} from "../../../store/api/admin/bankSlice";
import { getErrorMessage } from "../../../utils";
import AddBankDetails from "./AddBankDetails";

const List = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const location = useLocation();
  // USER role from slug
  const role =
    slug === "school-bank-details"
      ? "school"
      : slug === "vendor-bank-details"
        ? "vendor"
        : "super_admin";

  const [show, setShow] = useState(false);
  const [editData, setEditData] = useState(null);
  const [search, setSearch] = useState("");
  const [banks, setBanks] = useState([]);
  const [togglingId, setTogglingId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    search: "",
    total: 0,
    totalPages: 0,
  });

  const { data, isSuccess, isLoading, isFetching, isError, error } =
    useGetBanksQuery({
      page: pagination.page,
      limit: pagination.limit,
      search: pagination.search,
      role,
    });

  const [updateBank] = useUpdateBankMutation();

  if (isError) {
    toast.dismiss();
    toast.error(getErrorMessage(error));
  }

  useEffect(() => {
    if (isSuccess && data?.data) {
      setBanks(data.data.banks ?? []);
      const { total, page, limit, totalPages } = data.data.pagination;
      setPagination((prev) => ({ ...prev, total, page, limit, totalPages }));
    }
  }, [data, isSuccess]);

  // Clear search filter when input is cleared
  useEffect(() => {
    if (!search.trim()) {
      setPagination((prev) => ({ ...prev, search: "", page: 1 }));
    }
  }, [search]);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, search: search.trim(), page: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  };

  const handleClose = () => {
    setShow(false);
    setEditData(null);
  };

  const handleEdit = (bank) => {
    setEditData(bank);
    setShow(true);
  };

  const handleOpenModal = () => {
    setShow(true);
  };

  const handleToggleStatus = async (bank) => {
    toast.dismiss();
    const newStatus = bank.status === "active" ? "inactive" : "active";
    setTogglingId(bank.id);
    try {
      const res = await updateBank({
        id: bank.id,
        data: { status: newStatus },
      }).unwrap();
      toast.success(res?.message || t("updatedSuccessfully"));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")}: {pagination.total}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {!location.pathname.includes("/bank-details") && (
            <div className="d-flex flex-grow-1">
              <input
                type="text"
                className="form-control custom-field-one"
                placeholder={`${t("search")}...`}
                value={search}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
          <div className="d-flex gap-2 flex-grow-1">
            {!location.pathname.includes("/bank-details") && (
              <button
                className="btn btn-primary flex-grow-1"
                onClick={handleSearch}
              >
                {t("search")}
              </button>
            )}
            <Button
              className="d-flex align-items-center gap-1 flex-grow-1 justify-content-center"
              onClick={handleOpenModal}
            >
              <IoIosAddCircle size={15} fill="white" />
              {t("add")} {t("bank")}
            </Button>
          </div>
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>{`${t("Sr")} ${t("no")}`}</th>
              <th>{`${t("user")} ${t("name")}`}</th>
              <th>{`${t("bank")} ${t("name")}`}</th>
              <th>{t("omni_api_key")}</th>
              <th>{t("merchant_id")}</th>
              <th>{t("payment_method_id")}</th>
              <th>{t("status")}</th>
              <th>{t("action")}</th>
            </tr>
          </thead>

          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading MainLength={5} length={8} />
            ) : banks.length > 0 ? (
              banks.map((bank, i) => (
                <tr key={bank.id}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td className="text-nowrap">
                    {bank.user_full_name || "N/A"}
                  </td>
                  <td className="text-nowrap">{bank.bank_name || "N/A"}</td>
                  <td>{bank.omni_api_key || "N/A"}</td>
                  <td>{bank.merchant_id || "N/A"}</td>
                  <td>{bank.payment_method_id || "N/A"}</td>
                  <td>
                    <ToggleSwitch
                      checked={bank.status === "active"}
                      disabled={togglingId === bank.id}
                      onChange={() => handleToggleStatus(bank)}
                      title={
                        bank.status === "active"
                          ? t("click_to_deactivate") || "Deactivate"
                          : t("click_to_activate") || "Activate"
                      }
                    />
                  </td>
                  <td>
                    <div className="actionBtn d-flex">
                      <button
                        title={t("edit")}
                        className="btn btn-primary rounded-circle me-2"
                        onClick={() => handleEdit(bank)}
                      >
                        <BiEdit />
                      </button>

                      {/* <button
                        title={t("btns.delete")}
                        className="btn btn-primary rounded-circle"
                        onClick={() => alert(`Deleting ${bank.user_full_name}`)}
                      >
                        <RiDeleteBin6Fill />
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan="8">{`${t("no")} ${t("data")} ${t("found")}`}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      <AddBankDetails
        show={show}
        close={handleClose}
        editData={editData}
        role={role}
      />
    </>
  );
};

export default List;
